import { AcquisitionCapture } from './components/growth/AcquisitionCapture';
import { lazy, Suspense, Fragment } from "react";
import { Toaster } from "@/components/ui/sonner";
import { BuildUpdateNotice } from "@/components/system/BuildUpdateNotice";
import { ForceRefreshButton } from "@/components/system/ForceRefreshButton";
import { usePreviewRemountKey } from "@/hooks/usePreviewRemountKey";


import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { usePricingSkus } from "@/hooks/usePricingSkus";
import { reportAuthError } from "@/lib/api/authErrors";
import { AuthErrorGate } from "@/components/system/AuthErrorGate";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { isPhase1GatedPath, PHASE_1_LEARNER_ONLY } from "./config/phase1";
import { ScrollToTop } from "./components/ScrollToTop";
// The signed-in shell (sidebar, command palette, notifications, FTU overlay,
// keyboard shortcuts) is only ever rendered under /app. Importing it eagerly
// dragged cmdk, date-fns and the whole navigation tree into the entry chunk
// that public pages like /learn download.
const AppLayout = lazy(() =>
  import("./components/layout/AppLayout").then((m) => ({ default: m.AppLayout })),
);
import { ThemeProvider } from "./components/ThemeProvider";
import { AppSettingsProvider } from "./contexts/AppSettingsContext";
import { DarkModeProvider } from "./contexts/DarkModeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { UserRoleProvider } from "./contexts/UserRoleContext";
import { useUserRole } from "./hooks/useUserRole";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useOfflineDetection } from "@/hooks/useOfflineDetection";
import { InternalToolGuard } from "@/components/guards/InternalToolGuard";
import { RequireAppAdmin } from "@/components/guards/RequireAppAdmin";
import { RequireStaff } from "@/components/guards/RequireStaff";
import { FlagRoute } from "@/components/guards/FlagRoute";
import { RouteFlagProvider } from "@/contexts/RouteFlagContext";

import { RequireAuth } from "@/components/auth/RequireAuth";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PublicOnly } from "@/components/auth/PublicOnly";

import { RouteLoader } from "@/components/RouteLoader";
import { ENABLE_AI_ASSISTANT_UI } from "./config/featureFlagsCentral";
import { SkipLink } from "@/components/ui/skip-link";

import { RedirectRoute } from "./components/redirects/RedirectRoute";
import {
  PathwayCertificateCheckoutRedirect,
} from "./components/redirects/PricingLandingRedirect";
import { AudienceRedirect } from "./components/redirects/AudienceRedirect";
import { TokenRedirect } from "./components/redirects/TokenRedirect";
import { StudioPaneRedirect } from "./components/admin/StudioPaneRedirect";
import { LearnerPreviewBanner } from "./components/layout/LearnerPreviewBanner";



// Eagerly loaded - critical path pages
// LandingPage and UnifiedAuth are route-split: they are large (hero art,
// editorial sections, auth flows) and were previously bundled into the shared
// entry chunk, so every route — /learn included — paid for them. main.tsx warms
// the matching chunk in parallel with the entry script when the first paint is
// "/" or an auth route, so splitting them costs no extra round trip there.
const LandingPage = lazy(() => import("./pages/LandingPage"));
const UnifiedAuth = lazy(() => import("./pages/UnifiedAuth"));
import NotFound from "./pages/NotFound";

// Lazy loaded - Public pages
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Pricing = lazy(() => import("./pages/Pricing"));
const COPPANotice = lazy(() => import("./pages/COPPANotice"));

const About = lazy(() => import("./pages/About"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));

const Contact = lazy(() => import("./pages/Contact"));
const EmailUnsubscribe = lazy(() => import("./pages/EmailUnsubscribe"));
const Accessibility = lazy(() => import("./pages/Accessibility"));

const FAQ = lazy(() => import("./pages/FAQ"));
const Learn = lazy(() => import("./pages/Learn"));
const PathwayDetail = lazy(() => import("./pages/PathwayDetail"));
const PipelinePathwayPreview = lazy(() => import("./pages/PipelinePathwayPreview"));
const ModuleReader = lazy(() => import("./pages/ModuleReader"));

const MyPlan = lazy(() => import("./pages/MyPlan"));
const Saved = lazy(() => import("./pages/Saved"));


const PathwayCapstone = lazy(() => import("./pages/PathwayCapstone"));
const ProgramPack = lazy(() => import("./pages/ProgramPack"));
const LearnerJourney = lazy(() => import("./pages/LearnerJourney"));
const PathwayExam = lazy(() => import("./pages/PathwayExam"));
const Rewards = lazy(() => import("./pages/Rewards"));
const Community = lazy(() => import("./pages/Community"));
const AdminApiPartners = lazy(() => import("./pages/AdminApiPartners"));
const EmbedPocAdmin = lazy(() => import("./pages/admin/EmbedPocAdmin"));
const EmbedModule = lazy(() => import("./pages/EmbedModule"));
const PathwayCertificatePage = lazy(() => import("./pages/PathwayCertificatePage"));

const Trust = lazy(() => import("./pages/Trust"));
const ForInstitutions = lazy(() => import("./pages/ForInstitutions"));
const RegionalCatalog = lazy(() => import("./pages/RegionalCatalog"));


const FrameworksIndex = lazy(() => import("./pages/FrameworksIndex"));

const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AgeGate = lazy(() => import("./pages/AgeGate"));
const ArenaSsoStart = lazy(() => import("./pages/ArenaSsoStart"));
const ArenaSsoCallback = lazy(() => import("./pages/ArenaSsoCallback"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));

// Lazy loaded - App pages (inside /app shell)
const Settings = lazy(() => import("./pages/Settings"));


const AdminEntry = lazy(() => import("./pages/AdminEntry"));
const LearnerHome = lazy(() => import("./pages/LearnerHome"));
const Welcome = lazy(() => import("./pages/Welcome"));
const CertificatesWallet = lazy(() => import("./pages/CertificatesWallet"));
const Purchases = lazy(() => import("./pages/Purchases"));
const PathwayStudio = lazy(() => import("./pages/admin/PathwayStudio"));
const PathwayFilmStudio = lazy(() => import("./pages/admin/PathwayFilmStudio"));

const AppBrowse = lazy(() => import("./pages/AppBrowse"));
const SupportRequest = lazy(() => import("./pages/SupportRequest"));
const CapabilitiesAdmin = lazy(() => import("./pages/CapabilitiesAdmin"));
const FeatureManagementPage = lazy(() => import("./pages/FeatureManagementPage"));
const CurriculumDocumentPage = lazy(() => import("./pages/CurriculumDocumentPage"));

const LearningObjectsPage = lazy(() => import("./pages/LearningObjectsPage"));
const BadgeVerifyPage = lazy(() => import("./pages/BadgeVerifyPage"));
const ExpertReview = lazy(() => import("./pages/ExpertReview"));


// Lazy loaded - Admin pages
const SystemStatusPage = lazy(() => import("./pages/SystemStatusPage"));

const FeatureAdoptionDashboard = lazy(() => import("./pages/FeatureAdoptionDashboard"));

const AdminChangelog = lazy(() => import("./pages/AdminChangelog"));
const AdminKnowledgeBase = lazy(() => import("./pages/AdminKnowledgeBase"));

const OperationsHub = lazy(() => import("./pages/OperationsHub"));
const RequestAccess = lazy(() => import("./pages/RequestAccess"));
const NotifyEnrollment = lazy(() => import("./pages/NotifyEnrollment"));

const CurriculumPreview = lazy(() => import("./pages/admin/CurriculumPreview"));
const EngineLab = lazy(() => import("./pages/admin/EngineLab"));
const CertificationAdmin = lazy(() => import("./pages/admin/CertificationAdmin"));
const PricingAdmin = lazy(() => import("./pages/admin/PricingAdmin"));
const PointsAdmin = lazy(() => import("./pages/admin/PointsAdmin"));
const PathwayAuditReport = lazy(() => import("./pages/admin/PathwayAuditReport"));
const AuthoringProcessAuditReport = lazy(() => import("./pages/admin/AuthoringProcessAuditReport"));
const CommunitySettings = lazy(() => import("./pages/admin/CommunitySettings"));
const EndorsementsAdmin = lazy(() => import("./pages/admin/EndorsementsAdmin"));
const InviteAdmin = lazy(() => import("./pages/admin/InviteAdmin"));
const ExamIntegrity = lazy(() => import("./pages/admin/ExamIntegrity"));
const CapstoneReview = lazy(() => import("./pages/admin/CapstoneReview"));
const PractitionerSignoff = lazy(() => import("./pages/admin/PractitionerSignoff"));
const EducatorEditorialSignoff = lazy(() => import("./pages/admin/EducatorEditorialSignoff"));
const MediaProduction = lazy(() => import("./pages/admin/MediaProduction"));
const ExperienceBaseline = lazy(() => import("./pages/admin/ExperienceBaseline"));
const VoiceSettings = lazy(() => import("./pages/admin/VoiceSettings"));
const Cas1Triage = lazy(() => import("./pages/admin/Cas1Triage"));
const ExceptionsInbox = lazy(() => import("./pages/admin/ExceptionsInbox"));
const AdminTileRules = lazy(() => import("./pages/admin/TileRules"));
const AdminPathwayAnalytics = lazy(() => import("./pages/admin/PathwayAnalytics"));
const AdminImpersonation = lazy(() => import("./pages/admin/Impersonation"));
const LaunchSLOs = lazy(() => import("./pages/admin/LaunchSLOs"));
const DisputesQueue = lazy(() => import("./pages/admin/DisputesQueue"));
const PathwayCheckout = lazy(() => import("./pages/PathwayCheckout"));
const CheckoutConfirmation = lazy(() => import("./pages/CheckoutConfirmation"));
const BgmiLanding = lazy(() => import("./pages/BgmiLanding"));
const FreeFireLanding = lazy(() => import("./pages/FreeFireLanding"));



const ActivityFeed = lazy(() => import("./pages/ActivityFeed"));

// Lazy loaded - Billing Module

// Lazy loaded - AI Assistant (feature-flagged)
const AIAssistant = lazy(() => import("./components/assistant/AIAssistant"));
const EventOpsReviewQueue = lazy(() => import("./pages/EventOpsReviewQueue"));
const EventOpsReviewDetail = lazy(() => import("./pages/EventOpsReviewDetail"));


const queryClient = new QueryClient({
  // Any 401/403 from a query or mutation raises one friendly app-level screen
  // instead of leaving broken panels behind.
  queryCache: new QueryCache({
    onError: (error) => reportAuthError(error),
  }),
  mutationCache: new MutationCache({
    onError: (error) => reportAuthError(error),
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      // Refetch on window focus to catch updates after tab switches
      refetchOnWindowFocus: true,
      // Shorter stale time - data goes stale faster, encouraging fresh fetches
      staleTime: 60 * 1000, // 1 minute (was 5 minutes)
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
      // Refetch on reconnect to handle network blips
      refetchOnReconnect: true,
    },
  },
});

// Suspense wrapper helper for cleaner route definitions
const SuspenseRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<RouteLoader />}>{children}</Suspense>
);

/**
 * The assistant is only usable when signed in (every mode reads the caller's
 * role and org), so mounting it on public marketing/catalog pages downloaded a
 * ~40 KB chunk plus its hooks for visitors who could never open it. Gating on
 * the session keeps /learn and the homepage free of it.
 */
const AssistantMount = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (!ENABLE_AI_ASSISTANT_UI || isLoading || !isAuthenticated) return null;
  return (
    <Suspense fallback={null}>
      <AIAssistant />
    </Suspense>
  );
};



/**
 * Hydrates the pricing map from `public.pricing_skus` and remounts the tree
 * only when a rendered price actually changed, so marketing copy can never
 * drift from the Pricing & Promos admin.
 */
const PricingSync = ({ children }: { children: React.ReactNode }) => {
  const { version } = usePricingSkus();
  return <Fragment key={version}>{children}</Fragment>;
};

const AppContent = () => {
  useOfflineDetection();
  const location = useLocation();
  const { isAppAdmin, isOrgAdmin, isLoading: roleLoading } = useUserRole();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Phase 1: Individual Learners only. Every non-learner surface
  // (Educator, Institution, Partner API, Admin, /app/*) is redirected
  // to the public learner catalog until the flag is lifted.
  // Admins bypass this restriction so they can reach /app/admin/* tools.
  const gateReady = !authLoading && (!isAuthenticated || !roleLoading);
  const adminBypass = isAppAdmin || isOrgAdmin;
  // Signed-out visitors hitting an /app/* staff surface must land on the login
  // screen (with their destination preserved) rather than being silently
  // bounced to the public catalog — the route guards handle it from there.
  const signedOutAppSurface = !authLoading && !isAuthenticated && location.pathname.startsWith("/app");
  if (
    PHASE_1_LEARNER_ONLY &&
    gateReady &&
    !adminBypass &&
    !signedOutAppSurface &&
    isPhase1GatedPath(location.pathname)
  ) {
    return <Navigate to="/learn" replace />;
  }



  return (
    <>
    <LearnerPreviewBanner />
    <Routes>

      {/* Public Routes - Critical path eagerly loaded (visitors only) */}
      <Route path="/" element={<PublicOnly><SuspenseRoute><LandingPage /></SuspenseRoute></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><SuspenseRoute><UnifiedAuth /></SuspenseRoute></PublicOnly>} />
      {/* /welcome is the signed-in goal picker (LearnerHome sends first-time
          learners here). It must NOT be PublicOnly, or authed users bounce
          /welcome -> /app/home -> /welcome forever. */}
      <Route path="/welcome" element={<SuspenseRoute><Welcome /></SuspenseRoute>} />

      

      {/* Public Routes - Lazy loaded */}
      <Route path="/flyer" element={<RedirectRoute to="/learn" />} />
      <Route path="/pathway" element={<RedirectRoute to="/learn" />} />
      
      
      <Route path="/launch-cohort" element={<RedirectRoute to="/learn" />} />
      <Route path="/confirm-waitlist" element={<RedirectRoute to="/learn" />} />
      
      <Route path="/about" element={<PublicOnly><SuspenseRoute><About /></SuspenseRoute></PublicOnly>} />
      <Route path="/news" element={<RedirectRoute to="/" />} />
      <Route path="/unsubscribe" element={<SuspenseRoute><Unsubscribe /></SuspenseRoute>} />
      <Route path="/email-preferences" element={<SuspenseRoute><EmailPreferences /></SuspenseRoute>} />
      <Route path="/whats-new" element={<RedirectRoute to="/" />} />
      <Route path="/faq" element={<PublicOnly><SuspenseRoute><FAQ /></SuspenseRoute></PublicOnly>} />
      <Route path="/learn" element={<PublicOnly redirectTo="/app/browse"><SuspenseRoute><Learn /></SuspenseRoute></PublicOnly>} />
      {/* Partner deep-link entry (Arena ID `catalog`): resolves per audience, keeps utm params */}
      <Route path="/browse" element={<AudienceRedirect publicTo="/learn" authedTo="/app/browse" />} />
      <Route path="/bgmi" element={<FlagRoute flag="route_bgmi"><PublicOnly redirectTo="/app/browse"><SuspenseRoute><BgmiLanding /></SuspenseRoute></PublicOnly></FlagRoute>} />

      <Route path="/free-fire" element={<FlagRoute flag="route_free_fire"><PublicOnly redirectTo="/app/browse"><SuspenseRoute><FreeFireLanding /></SuspenseRoute></PublicOnly></FlagRoute>} />

      <Route path="/learn/coming-soon/:slug" element={<SuspenseRoute><PipelinePathwayPreview /></SuspenseRoute>} />
      <Route path="/learn/:slug" element={<SuspenseRoute><PathwayDetail /></SuspenseRoute>} />
      <Route path="/learn/:slug/module/:moduleId" element={<SuspenseRoute><ModuleReader /></SuspenseRoute>} />
      {/* Consolidated: /my-learning now redirects to unified learner home */}
      <Route path="/my-learning" element={<RedirectRoute to="/app/home?tab=enrollments" />} />
      {/* /plan and /saved removed from primary nav (accessed via cart / wishlist indicators only) */}
      <Route path="/plan" element={<RedirectRoute to="/app/plan" />} />
      <Route path="/saved" element={<RedirectRoute to="/app/saved" />} />


      <Route path="/learn/:slug/exam" element={<RequireAuth><SuspenseRoute><PathwayExam /></SuspenseRoute></RequireAuth>} />
      <Route path="/learn/:slug/capstone" element={<FlagRoute flag="route_capstone"><RequireAuth><SuspenseRoute><PathwayCapstone /></SuspenseRoute></RequireAuth></FlagRoute>} />
      <Route path="/learn/:slug/program-pack" element={<RequireAuth><SuspenseRoute><ProgramPack /></SuspenseRoute></RequireAuth>} />
      <Route path="/learn/:slug/journey" element={<RequireAuth><SuspenseRoute><LearnerJourney /></SuspenseRoute></RequireAuth>} />
      <Route path="/learn/:slug/placement" element={<RedirectRoute to="/learn" />} />
      <Route path="/certifications" element={<RedirectRoute to="/learn?status=open#catalog" />} />
      {/* Public pricing page — region-aware ($49 US / ₹2,999 India WORK band,
          flat gamer ladder in India). Cert-checkout keeps the pathway landing. */}
      <Route path="/pricing" element={<SuspenseRoute><Pricing /></SuspenseRoute>} />
      <Route path="/learn/:slug/certificate/checkout" element={<PathwayCertificateCheckoutRedirect />} />
      <Route
        path="/checkout/pathway/:slug"
        element={<FlagRoute flag="route_checkout"><SuspenseRoute><PathwayCheckout /></SuspenseRoute></FlagRoute>}
      />
      <Route
        path="/checkout/confirmation/:orderId"
        element={<RequireAuth><SuspenseRoute><CheckoutConfirmation /></SuspenseRoute></RequireAuth>}
      />



      <Route path="/rewards" element={<RedirectRoute to="/app/rewards" />} />
      <Route path="/connect/arena-id" element={<RedirectRoute to="/app/settings?tab=arena" />} />
      {/* Phase 1 (Individual Learners only): legacy public surfaces
          collapse to /learn. India, Regions, Educators, Institutions,
          and Partner API are hidden until Phase 2. */}
      <Route path="/for-organizations" element={<SuspenseRoute><ForInstitutions /></SuspenseRoute>} />
      <Route path="/for-institutions" element={<RedirectRoute to="/for-organizations" />} />
      <Route path="/for-educators" element={<RedirectRoute to="/learn/esports-educator" />} />
      <Route path="/for-coaches" element={<RedirectRoute to="/learn" />} />
      <Route path="/for-parents" element={<RedirectRoute to="/learn?category=caregiver" />} />
      
      <Route path="/india" element={<RedirectRoute to="/frameworks?region=IN" />} />
      <Route path="/india-coverage" element={<RedirectRoute to="/frameworks?region=IN" />} />
      <Route path="/regions" element={<RedirectRoute to="/learn" />} />
      {/* US framework + price view (payment still closed) — before the catch-all. */}
      <Route path="/regions/india" element={<PublicOnly redirectTo="/app/browse"><SuspenseRoute><RegionalCatalog market="india" /></SuspenseRoute></PublicOnly>} />
      <Route path="/regions/united-states" element={<PublicOnly redirectTo="/app/browse"><SuspenseRoute><RegionalCatalog market="us" /></SuspenseRoute></PublicOnly>} />
      <Route path="/regions/us" element={<RedirectRoute to="/regions/united-states" />} />
      <Route path="/regions/:region" element={<RedirectRoute to="/learn" />} />

      <Route path="/api" element={<RedirectRoute to="/learn" />} />
      <Route path="/developers" element={<RedirectRoute to="/learn" />} />
      <Route path="/trust-safety" element={<RedirectRoute to="/trust" />} />

      <Route path="/educator" element={<RedirectRoute to="/learn/esports-educator" />} />
      <Route path="/educator/capstone-inbox" element={<RedirectRoute to="/learn" />} />
      <Route path="/educator/roster" element={<RedirectRoute to="/learn" />} />
      <Route path="/esports-glossary" element={<RedirectRoute to="/learn" />} />
      <Route path="/trust" element={<PublicOnly><SuspenseRoute><Trust /></SuspenseRoute></PublicOnly>} />
      <Route path="/roadmap" element={<RedirectRoute to="/" />} />
      <Route path="/framework-coverage" element={<RedirectRoute to="/frameworks" />} />
      <Route path="/frameworks" element={<FlagRoute flag="route_frameworks"><SuspenseRoute><FrameworksIndex /></SuspenseRoute></FlagRoute>} />
      <Route path="/frameworks/crosswalk" element={<RedirectRoute to="/frameworks?tab=crosswalk" />} />
      <Route path="/app/frameworks" element={<RedirectRoute to="/frameworks?tab=coverage" />} />
      <Route path="/how-alignment-works" element={<RedirectRoute to="/frameworks" />} />
      <Route path="/how-it-works" element={<RedirectRoute to="/pricing" />} />



      <Route path="/privacy" element={<SuspenseRoute><PrivacyPolicy /></SuspenseRoute>} />
      <Route path="/terms" element={<SuspenseRoute><TermsOfService /></SuspenseRoute>} />
      <Route path="/refunds" element={<SuspenseRoute><RefundPolicy /></SuspenseRoute>} />
      <Route path="/coppa" element={<SuspenseRoute><COPPANotice /></SuspenseRoute>} />
      <Route path="/accessibility" element={<SuspenseRoute><Accessibility /></SuspenseRoute>} />
      <Route path="/contact" element={<SuspenseRoute><Contact /></SuspenseRoute>} />
      <Route path="/email-unsubscribe" element={<SuspenseRoute><EmailUnsubscribe /></SuspenseRoute>} />

      
      <Route path="/request-access" element={<SuspenseRoute><RequestAccess /></SuspenseRoute>} />
      <Route path="/notify" element={<SuspenseRoute><NotifyEnrollment /></SuspenseRoute>} />
      <Route path="/request-demo" element={<RedirectRoute to="/learn" />} />
      <Route path="/coach-join" element={<RedirectRoute to="/learn" />} />
      <Route path="/reset-password" element={<SuspenseRoute><ResetPassword /></SuspenseRoute>} />
      <Route path="/forgot-password" element={<PublicOnly><SuspenseRoute><ForgotPassword /></SuspenseRoute></PublicOnly>} />
      <Route path="/auth/callback" element={<SuspenseRoute><AuthCallback /></SuspenseRoute>} />
      <Route path="/signin" element={<RedirectRoute to="/auth" />} />
      <Route path="/my_learning" element={<RedirectRoute to="/app" />} />
      <Route path="/sso/arena" element={<SuspenseRoute><ArenaSsoStart /></SuspenseRoute>} />
      {/* Arena ID callback: single allow-listed path. Legacy ordering forwards (code/state preserved). */}
      <Route path="/auth/callback/arena" element={<SuspenseRoute><ArenaSsoCallback /></SuspenseRoute>} />
      <Route path="/auth/arena/callback" element={<RedirectRoute to="/auth/callback/arena" />} />
      <Route path="/pending-approval" element={<SuspenseRoute><PendingApproval /></SuspenseRoute>} />
      {/* Token-gated, read-only pathway transcript for external expert reviewers */}
      <Route path="/review/:token" element={<SuspenseRoute><ExpertReview /></SuspenseRoute>} />
      <Route path="/embed/:slug/module/:moduleId" element={<SuspenseRoute><EmbedModule /></SuspenseRoute>} />
      {/* Credential verify: canonical is /credentials/verify/:token; /verify/:token is the short public alias */}
      <Route path="/verify/:token" element={<TokenRedirect to="/credentials/verify/:token" />} />

      <Route path="/credentials/verify/:token" element={<FlagRoute flag="route_credentials"><SuspenseRoute><BadgeVerifyPage /></SuspenseRoute></FlagRoute>} />
      {/* Credential view: canonical is /credentials/:token; legacy /certificate/:token redirects */}
      <Route path="/certificate/:token" element={<TokenRedirect to="/credentials/:token" />} />
      <Route path="/credentials/:token" element={<FlagRoute flag="route_credentials"><SuspenseRoute><PathwayCertificatePage /></SuspenseRoute></FlagRoute>} />



      
      {/* Legacy redirects */}
      <Route path="/home" element={<RedirectRoute to="/" />} />
      
      {/* /app/* convenience redirects for common paths */}
      <Route path="/auth" element={<PublicOnly><SuspenseRoute><UnifiedAuth /></SuspenseRoute></PublicOnly>} />
      <Route path="/auth/age" element={<SuspenseRoute><AgeGate /></SuspenseRoute>} />
      <Route path="/privacy-policy" element={<RedirectRoute to="/privacy" />} />
      <Route path="/terms-of-service" element={<RedirectRoute to="/terms" />} />
      <Route path="/coppa-notice" element={<RedirectRoute to="/coppa" />} />
      
      {/* /app path redirects - These handle /app/X redirects to root-level /X routes */}
      <Route path="/app/privacy" element={<RedirectRoute to="/privacy" />} />
      <Route path="/app/terms" element={<RedirectRoute to="/terms" />} />
      <Route path="/app/accessibility" element={<RedirectRoute to="/accessibility" />} />
      <Route path="/app/request-access" element={<RedirectRoute to="/learn" />} />
      <Route path="/app/whats-new" element={<RedirectRoute to="/" />} />
      
      {/* Protected App Shell - layout and children both lazy */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <SuspenseRoute><AppLayout /></SuspenseRoute>
          </ProtectedRoute>
        }
      >
        {/* Learner + Admin shell (Phase 1 in-scope). Legacy Tier 1/3
            surfaces (players/coaches/parents/teams/drills/sessions/
            welfare/skill-transfer/educator/collegiate/league/
            nav-hubs/etc.) redirect to /app/admin for one release. */}
        <Route index element={<SuspenseRoute><AdminEntry /></SuspenseRoute>} />
        <Route path="home" element={<SuspenseRoute><LearnerHome /></SuspenseRoute>} />
        <Route path="browse" element={<SuspenseRoute><AppBrowse /></SuspenseRoute>} />
        {/* Authenticated pathway surfaces — keep learner sidebar chrome.
            Public shareable URLs remain /learn/:slug (outside this shell). */}
        <Route path="pathways/:slug" element={<SuspenseRoute><PathwayDetail /></SuspenseRoute>} />
        <Route path="pathways/:slug/module/:moduleId" element={<SuspenseRoute><ModuleReader /></SuspenseRoute>} />
        <Route path="pathways/:slug/program-pack" element={<SuspenseRoute><ProgramPack /></SuspenseRoute>} />
        <Route path="pathways/:slug/journey" element={<SuspenseRoute><LearnerJourney /></SuspenseRoute>} />
        {/* Same flag and auth policy as /learn/:slug/capstone; PathwayCapstone dispatches the assessment route. */}
        <Route path="pathways/:slug/capstone" element={<FlagRoute flag="route_capstone"><RequireAuth><SuspenseRoute><PathwayCapstone /></SuspenseRoute></RequireAuth></FlagRoute>} />
        {/* Legacy /app/dashboard: role-aware — admins get AdminHub, learners land on learner home
            (avoids the double silent redirect through the phase-1 admin gate). */}
        <Route path="dashboard" element={<SuspenseRoute><AdminEntry /></SuspenseRoute>} />
        <Route path="settings" element={<SuspenseRoute><Settings /></SuspenseRoute>} />
        <Route path="eventops-review/:pathwayId" element={<SuspenseRoute><EventOpsReviewQueue /></SuspenseRoute>} />
        <Route path="eventops-review/:pathwayId/:submissionId" element={<SuspenseRoute><EventOpsReviewDetail /></SuspenseRoute>} />
        <Route path="certificates" element={<SuspenseRoute><CertificatesWallet /></SuspenseRoute>} />
        <Route path="purchases" element={<SuspenseRoute><Purchases /></SuspenseRoute>} />
        <Route path="support" element={<SuspenseRoute><SupportRequest /></SuspenseRoute>} />
        <Route path="rewards" element={<FlagRoute flag="route_rewards"><SuspenseRoute><Rewards /></SuspenseRoute></FlagRoute>} />
        <Route path="community" element={<SuspenseRoute><Community /></SuspenseRoute>} />
        <Route path="saved" element={<SuspenseRoute><Saved /></SuspenseRoute>} />
        <Route path="plan" element={<SuspenseRoute><MyPlan /></SuspenseRoute>} />

        {/* Admin console */}
        <Route path="admin" element={<SuspenseRoute><AdminEntry /></SuspenseRoute>} />
        <Route path="admin-center" element={<Navigate to="/app/admin" replace />} />
        <Route path="admin-hub" element={<Navigate to="/app/admin" replace />} />
        <Route path="platform-admin" element={<Navigate to="/app/admin" replace />} />
        <Route path="admin/pathway-studio" element={<RequireAppAdmin><SuspenseRoute><PathwayStudio /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/pathway-film" element={<RequireAppAdmin><SuspenseRoute><PathwayFilmStudio /></SuspenseRoute></RequireAppAdmin>} />

        {/* P5.5 — standalone curriculum console bookmarks land in Studio curriculum pane. */}
        <Route path="admin/curriculum-console" element={<RequireStaff><StudioPaneRedirect /></RequireStaff>} />
        <Route path="admin/exam-integrity" element={<RequireAppAdmin><SuspenseRoute><ExamIntegrity /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/capstone-review" element={<RequireAppAdmin><SuspenseRoute><CapstoneReview /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/practitioner-signoff" element={<SuspenseRoute><PractitionerSignoff /></SuspenseRoute>} />
        <Route path="admin/editorial-signoff" element={<RequireAppAdmin><SuspenseRoute><EducatorEditorialSignoff /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/media-production" element={<RequireAppAdmin><SuspenseRoute><MediaProduction /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/experience-baseline" element={<RequireAppAdmin><SuspenseRoute><ExperienceBaseline /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/invites" element={<RequireAppAdmin><SuspenseRoute><InviteAdmin /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/pricing" element={<RequireAppAdmin><SuspenseRoute><PricingAdmin /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/community" element={<RequireAppAdmin><SuspenseRoute><CommunitySettings /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/points" element={<RequireAppAdmin><SuspenseRoute><PointsAdmin /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/audit-report" element={<RequireAppAdmin><SuspenseRoute><PathwayAuditReport /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/process-audit" element={<RequireAppAdmin><SuspenseRoute><AuthoringProcessAuditReport /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/endorsements" element={<RequireAppAdmin><SuspenseRoute><EndorsementsAdmin /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/expert-reviews" element={<Navigate to="/app/admin/pathway-studio?stage=review&pane=review&tab=expert" replace />} />
        <Route path="admin/conversion" element={<Navigate to="/app/admin/launch-slos?tab=conversion" replace />} />

        <Route path="admin/certifications" element={<RequireAppAdmin><SuspenseRoute><CertificationAdmin /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/voice-settings" element={<RequireAppAdmin><SuspenseRoute><VoiceSettings /></SuspenseRoute></RequireAppAdmin>} />
        {/* Authoring runs inside the gated Pathway Studio workflow. */}
        <Route path="admin/content-studio" element={<Navigate to="/app/admin/pathway-studio?stage=author" replace />} />
        {/* P5.5 — standalone review/audits bookmarks → Studio panes (pages remain Studio embeds). */}
        <Route path="admin/review" element={<RequireAppAdmin><StudioPaneRedirect /></RequireAppAdmin>} />
        <Route path="admin/audits" element={<RequireAppAdmin><StudioPaneRedirect /></RequireAppAdmin>} />
        <Route path="admin/content-studio/review" element={<Navigate to="/app/admin/pathway-studio?stage=review" replace />} />
        <Route path="admin/impersonation" element={<RequireAppAdmin><SuspenseRoute><AdminImpersonation /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/pathway-analytics" element={<RequireAppAdmin><SuspenseRoute><AdminPathwayAnalytics /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/tile-rules" element={<RequireAppAdmin><SuspenseRoute><AdminTileRules /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/cas1-triage" element={<RequireAppAdmin><SuspenseRoute><Cas1Triage /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/exceptions" element={<RequireAppAdmin><SuspenseRoute><ExceptionsInbox /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/curriculum-preview/:entityType/:idOrSlug" element={<RequireStaff><SuspenseRoute><CurriculumPreview /></SuspenseRoute></RequireStaff>} />
        <Route path="admin/engine-lab" element={<RequireStaff><SuspenseRoute><EngineLab /></SuspenseRoute></RequireStaff>} />
        <Route path="admin/api-partners" element={<RequireAppAdmin><SuspenseRoute><AdminApiPartners /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/embed-poc" element={<RequireAppAdmin><SuspenseRoute><EmbedPocAdmin /></SuspenseRoute></RequireAppAdmin>} />

        {/* Curriculum authoring */}
        <Route path="curriculum-document" element={<RequireStaff><SuspenseRoute><CurriculumDocumentPage /></SuspenseRoute></RequireStaff>} />
        
        <Route path="curriculum-mapping-review" element={<Navigate to="/app/admin/pathway-studio?stage=review&pane=review&tab=crosswalk" replace />} />
        <Route path="learning-objects" element={<RequireAppAdmin><SuspenseRoute><LearningObjectsPage /></SuspenseRoute></RequireAppAdmin>} />
        

        {/* Admin ops & platform */}
        <Route path="audit-log" element={<Navigate to="/app/admin/pathway-studio?stage=publish&pane=audits&tab=log" replace />} />
        <Route path="system-status" element={<RequireAppAdmin><SuspenseRoute><SystemStatusPage /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/alignment-scans" element={<Navigate to="/app/admin/pathway-studio?stage=publish&pane=audits&tab=alignment" replace />} />
        <Route path="admin/launch-slos" element={<RequireAppAdmin><SuspenseRoute><LaunchSLOs /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="admin/disputes" element={<RequireAppAdmin><SuspenseRoute><DisputesQueue /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="daily-audit" element={<Navigate to="/app/admin/pathway-studio?stage=publish&pane=audits&tab=daily" replace />} />
        <Route path="capabilities" element={<RequireAppAdmin><SuspenseRoute><CapabilitiesAdmin /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="feature-management" element={<RequireAppAdmin><SuspenseRoute><FeatureManagementPage /></SuspenseRoute></RequireAppAdmin>} />
        
        <Route path="feature-adoption" element={<RequireAppAdmin><SuspenseRoute><FeatureAdoptionDashboard /></SuspenseRoute></RequireAppAdmin>} />
        
        <Route path="changelog" element={<RequireAppAdmin><SuspenseRoute><AdminChangelog /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="knowledge-base" element={<RequireStaff><SuspenseRoute><AdminKnowledgeBase /></SuspenseRoute></RequireStaff>} />
        <Route path="consent-audit" element={<Navigate to="/app/admin/pathway-studio?stage=publish&pane=audits&tab=consent" replace />} />
        <Route path="operations" element={<RequireAppAdmin><SuspenseRoute><OperationsHub /></SuspenseRoute></RequireAppAdmin>} />
        <Route path="incident-control" element={<Navigate to="/app/operations" replace />} />

        <Route path="documents" element={<Navigate to="/app/knowledge-base" replace />} />
        <Route path="user-manuals" element={<Navigate to="/app/knowledge-base" replace />} />
        <Route path="admin-impersonate" element={<Navigate to="/app/admin/impersonation" replace />} />
        
        <Route path="activity" element={<InternalToolGuard><SuspenseRoute><ActivityFeed /></SuspenseRoute></InternalToolGuard>} />

        {/* Subscription billing surfaces retired: GG Life sells one-time
            pathway unlocks only (docs/mvp_scope.md §5). */}


        {/* Retired surfaces: anything unmatched inside the app shell lands on
            the learner home (admins are routed on to the console by AdminEntry). */}
        <Route path="coach-profile" element={<RedirectRoute to="/app/settings" />} />
        <Route path="india-coverage" element={<RedirectRoute to="/frameworks?region=IN" />} />
        <Route path="*" element={<RedirectRoute to="/app/home" />} />

      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );

};

/**
 * ProtectedRoute - Uses AuthContext as single source of truth
 * No duplicate auth listeners - relies on centralized AuthProvider
 */



const App = () => {
  // Remounts the provider tree at the next route change after a new build.
  const remountKey = usePreviewRemountKey();

  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <Fragment key={remountKey}>
      <RouteFlagProvider>
      <AuthProvider>

        <UserRoleProvider>
          <DarkModeProvider>
            <AppSettingsProvider>
              <ThemeProvider>
                <TooltipProvider>
                  <Toaster />
                  <BuildUpdateNotice />
                  <ForceRefreshButton />

                  <BrowserRouter
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true,
                    }}
                  >
                    <AcquisitionCapture />
                    <SkipLink />
                    <ScrollToTop />
                    <Suspense fallback={null}>
                      <AuthErrorGate>
                        <PricingSync>
                          <AppContent />
                        </PricingSync>
                      </AuthErrorGate>
                    </Suspense>
                    {/* AI Assistant UI - lazy loaded, feature-flagged, signed-in only */}
                    <AssistantMount />
                    {/* MilestoneCelebrationProvider retired with legacy milestone tables (Pass 3) */}
                  </BrowserRouter>
                </TooltipProvider>
              </ThemeProvider>
            </AppSettingsProvider>
          </DarkModeProvider>
        </UserRoleProvider>
      </AuthProvider>
      </RouteFlagProvider>
      </Fragment>
    </QueryClientProvider>

  </ErrorBoundary>
  );
};


export default App;
